import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Trash2, ClipboardCheck, FileDown, HardDriveDownload } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { qualityControlReportService, productService, qmsPlanService, clientService, productionLotService, api } from '../services/api';
import { QcrStatusBadge } from '../components/AutosaveIndicator';

function defaultForm() {
  return {
    control_date: new Date().toISOString().slice(0, 10),
    customer_id: '',
    product_id: '',
    product_revision_id: '',
    production_lot_id: '',
    control_plan_id: '',
    control_situation: '',
    sample_count: 5,
    control_notes: '',
  };
}

export default function QualityControlReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [lots, setLots] = useState([]);
  const [plans, setPlans] = useState([]);
  const [availableSituations, setAvailableSituations] = useState([]);
  const [loadingSituations, setLoadingSituations] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    const res = await qualityControlReportService.list();
    setReports(res.data || []);
  }, []);

  useEffect(() => {
    load();
    productService.getAll({ limit: 1000 }).then(r => setProducts(r.data?.items || r.data || []));
    clientService.getAll({ limit: 500 }).then(r => setClients(r.data?.items || r.data || []));
    productionLotService.getAll({ limit: 500 }).then(r => setLots(r.data?.items || r.data || []));
    qmsPlanService.list({ limit: 500 }).then(r => setPlans(r.data?.items || r.data || []));
  }, [load]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    let base = products;
    if (form.customer_id) {
      base = base.filter(p => (p.client_id || '') === form.customer_id);
    }
    if (!q) return base.slice(0, 50);
    return base.filter(p =>
      (p.code || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q),
    ).slice(0, 50);
  }, [products, productSearch, form.customer_id]);

  const selectedProduct = useMemo(() => products.find(p => p.id === form.product_id), [products, form.product_id]);
  const selectedClient = useMemo(() => clients.find(c => c.id === form.customer_id), [clients, form.customer_id]);
  const selectedPlan = useMemo(() => plans.find(p => p.id === form.control_plan_id), [plans, form.control_plan_id]);
  const applicablePlans = useMemo(() => {
    if (!selectedProduct) return [];
    let list = plans.filter(p => (p.product_code || '') === (selectedProduct.code || ''));
    if (selectedClient) {
      const clientCode = selectedClient.code || selectedClient.customer_code || '';
      list = list.filter(p => (p.customer_code || '').toLowerCase() === clientCode.toLowerCase());
    }
    return list;
  }, [plans, selectedProduct, selectedClient]);

  // Fetch available control situations dynamically from the selected plan
  useEffect(() => {
    if (!form.control_plan_id) { setAvailableSituations([]); return; }
    setLoadingSituations(true);
    api.get(`/qms/plans/${form.control_plan_id}/available-situations`)
      .then(r => setAvailableSituations(r.data || []))
      .catch(e => { console.error('Load available-situations failed', e); setAvailableSituations([]); })
      .finally(() => setLoadingSituations(false));
    // reset selected situation whenever the plan changes
    setForm(f => ({ ...f, control_situation: '' }));
  }, [form.control_plan_id]);
  const applicableLots = useMemo(() => {
    if (!form.product_id) return lots;
    return lots.filter(l => l.product_id === form.product_id);
  }, [lots, form.product_id]);

  const filteredReports = useMemo(() => {
    let list = reports;
    if (statusFilter !== 'all') list = list.filter(r => (r.status || 'In compilazione') === statusFilter);
    if (!filter) return list;
    const q = filter.toLowerCase();
    return list.filter(r =>
      (r.report_number || '').toLowerCase().includes(q) ||
      (r.product_code || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.lot_code || '').toLowerCase().includes(q),
    );
  }, [reports, filter, statusFilter]);

  const submit = async () => {
    if (!form.product_id) return toast.error('Seleziona un prodotto');
    if (!form.control_plan_id) return toast.error('Seleziona il Piano di Controllo');
    if (!form.control_situation) return toast.error('Seleziona la Situazione di controllo');
    if (!form.sample_count || form.sample_count < 1 || form.sample_count > 100) return toast.error('Numero campioni tra 1 e 100');
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.sample_count = Number(form.sample_count);
      const res = await qualityControlReportService.create(payload);
      toast.success(`Report ${res.data.report_number} creato con ${res.data.sample_count} campioni`);
      setOpenNew(false); setForm(defaultForm());
      navigate(`/quality-control-reports/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione report');
    } finally { setLoading(false); }
  };

  const removeReport = async (id, num) => {
    if (!window.confirm(`Eliminare il report ${num}?`)) return;
    try { await qualityControlReportService.remove(id); toast.success('Report eliminato'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const badge = (r) => {
    if (r === 'OK') return <Badge className="bg-emerald-600 text-white">OK</Badge>;
    if (r === 'NOK') return <Badge className="bg-red-600 text-white">NOK</Badge>;
    return <Badge className="bg-slate-300 text-slate-700">In corso</Badge>;
  };

  const downloadPdf = async (id, num) => {
    try {
      const res = await qualityControlReportService.pdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${num}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore scaricamento PDF'); }
  };

  return (
    <>
      <div className="space-y-4" data-testid="qcr-list-page">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-indigo-600" />Registro Controlli Qualità</h1>
            <p className="text-xs md:text-sm text-slate-500">Report multi-campione con misure automatiche vs piano di controllo</p>
          </div>
          <Button className="h-11 md:h-10 w-full sm:w-auto" onClick={() => setOpenNew(true)} data-testid="new-qcr-btn"><Plus className="w-4 h-4 mr-1" />Nuovo Collaudo</Button>
        </div>

        <div className="bg-white rounded border p-3">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Search className="w-4 h-4 text-slate-400 hidden sm:block" />
            <Input placeholder="Cerca per numero, prodotto, cliente, lotto…" value={filter} onChange={e => setFilter(e.target.value)} className="w-full sm:max-w-md h-10 sm:h-8" data-testid="qcr-filter" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 sm:h-8 w-full sm:w-48 text-xs" data-testid="qcr-status-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">— Tutti gli stati —</SelectItem>
                <SelectItem value="Bozza">Bozza</SelectItem>
                <SelectItem value="In compilazione">In compilazione</SelectItem>
                <SelectItem value="Completato">Completato</SelectItem>
                <SelectItem value="Annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="qcr-mobile-list">
            {filteredReports.map(r => (
              <div
                key={r.id}
                className="border rounded-xl p-3 active:bg-slate-50"
                onClick={() => navigate(`/quality-control-reports/${r.id}`)}
                data-testid={`qcr-card-${r.report_number}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold text-indigo-700">{r.report_number}</span>
                  <div className="flex items-center gap-1"><QcrStatusBadge status={r.status} />{badge(r.overall_result)}</div>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">{r.product_code || '—'}</div>
                <div className="text-xs text-slate-500">{r.customer_name || '—'} · Lotto {r.lot_code || '—'}</div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                  <span>{r.control_date} · {r.sample_count} camp.</span>
                  <span>{r.measurements_total ? `${r.measurements_filled}/${r.measurements_total} misure` : ''}</span>
                </div>
                <div className="mt-2 flex gap-2" onClick={e => e.stopPropagation()}>
                  <Button variant="outline" className="h-10 flex-1" onClick={() => navigate(`/quality-control-reports/${r.id}`)} data-testid={`qcr-card-open-${r.report_number}`}>Apri</Button>
                  <Button variant="outline" className="h-10 w-12 p-0" onClick={() => downloadPdf(r.id, r.report_number)} aria-label="Scarica PDF" data-testid={`qcr-card-pdf-${r.report_number}`}><FileDown className="w-4 h-4" /></Button>
                  <Button variant="outline" className="h-10 w-12 p-0 text-red-600" onClick={() => removeReport(r.id, r.report_number)} aria-label="Elimina" data-testid={`qcr-card-delete-${r.report_number}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {filteredReports.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Nessun report.</div>}
          </div>

          <div className="overflow-auto hidden md:block">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Report</th><th className="p-2 text-left">Data</th><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-left">Prodotto</th><th className="p-2 text-left">Lotto</th>
                  <th className="p-2 text-center">Campioni</th><th className="p-2 text-center">OK</th><th className="p-2 text-center">NOK</th>
                  <th className="p-2 text-center">Stato</th><th className="p-2 text-center">Esito</th>
                  <th className="p-2 text-left">Ultimo salvataggio</th>
                  <th className="p-2 text-center">Archivio locale</th>
                  <th className="p-2 text-left">Ispettore</th><th className="p-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map(r => (
                  <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`qcr-row-${r.report_number}`}>
                    <td className="p-2 font-mono font-semibold text-indigo-700 cursor-pointer" onClick={() => navigate(`/quality-control-reports/${r.id}`)}>{r.report_number}</td>
                    <td className="p-2">{r.control_date}</td>
                    <td className="p-2"><Badge className="bg-slate-700 text-white text-[10px]">{r.control_type_label || r.control_situation || '—'}</Badge></td>
                    <td className="p-2">{r.customer_name || '—'}</td>
                    <td className="p-2"><span className="font-mono">{r.product_code || '—'}</span> {r.product_name && <span className="text-slate-500">— {r.product_name}</span>}</td>
                    <td className="p-2 font-mono">{r.lot_code || '—'}</td>
                    <td className="p-2 text-center">{r.sample_count}</td>
                    <td className="p-2 text-center text-emerald-700 font-semibold">{r.samples_ok}</td>
                    <td className="p-2 text-center text-red-700 font-semibold">{r.samples_nok}</td>
                    <td className="p-2 text-center">{<QcrStatusBadge status={r.status} />}</td>
                    <td className="p-2 text-center">{badge(r.overall_result)}</td>
                    <td className="p-2 text-slate-600" data-testid={`qcr-last-saved-${r.report_number}`}>
                      {r.last_saved_at ? new Date(r.last_saved_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      {r.measurements_total ? <span className="text-[10px] text-slate-400 block">{r.measurements_filled}/{r.measurements_total} misure</span> : null}
                    </td>
                    <td className="p-2 text-center" data-testid={`qcr-exported-${r.report_number}`}>
                      {r.exported_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700" title={r.export_target || ''}>
                          <HardDriveDownload className="w-3.5 h-3.5" />
                          {new Date(r.exported_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                        </span>
                      ) : r.status === 'Completato' ? (
                        <span className="text-amber-600 text-[10px]">in coda</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2">{r.inspector_name || '—'}</td>
                    <td className="p-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(r.id, r.report_number)} title="Scarica PDF" data-testid={`qcr-pdf-${r.report_number}`}><FileDown className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => removeReport(r.id, r.report_number)} title="Elimina" className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredReports.length === 0 && (
                  <tr><td colSpan={15} className="text-center py-8 text-slate-400">Nessun report. Clicca &quot;Nuovo Collaudo&quot; per iniziare.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={openNew} onOpenChange={(v) => { setOpenNew(v); if (!v) { setForm(defaultForm()); setProductSearch(''); } }}>
        <DialogContent className="max-w-2xl" data-testid="qcr-new-dialog">
          <DialogHeader><DialogTitle>Nuovo Collaudo Qualità</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data controllo *</Label>
                <Input type="date" value={form.control_date} onChange={e => setForm({ ...form, control_date: e.target.value })} />
              </div>
              <div>
                <Label>Cliente</Label>
                <Select value={form.customer_id || 'none'} onValueChange={v => setForm({ ...form, customer_id: v === 'none' ? '' : v, product_id: '', control_plan_id: '' })}>
                  <SelectTrigger data-testid="qcr-new-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tutti i clienti —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Prodotto *</Label>
              <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Filtra prodotti…" className="mb-1 h-8" data-testid="qcr-new-product-search" />
              <Select value={form.product_id || 'none'} onValueChange={v => setForm({ ...form, product_id: v === 'none' ? '' : v, control_plan_id: '', production_lot_id: '' })}>
                <SelectTrigger data-testid="qcr-new-product-select"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                <SelectContent>
                  {filteredProducts.length === 0 && (
                    <SelectItem value="none" disabled>
                      {selectedClient
                        ? `Nessun prodotto per ${selectedClient.name}`
                        : 'Nessun prodotto'}
                    </SelectItem>
                  )}
                  {filteredProducts.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono">{p.code}</span> — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClient && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Filtrato per cliente: <span className="font-semibold">{selectedClient.name}</span> ({filteredProducts.length} prodotti)
                </div>
              )}
              {selectedProduct && (
                <div className="text-xs text-slate-500 mt-1">Descrizione: {selectedProduct.description || selectedProduct.name}</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Lotto / Commessa</Label>
                <Select value={form.production_lot_id || 'none'} onValueChange={v => setForm({ ...form, production_lot_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="— opzionale —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {applicableLots.map(l => <SelectItem key={l.id} value={l.id}>{l.lot_code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Piano di Controllo *</Label>
                <Select value={form.control_plan_id || 'none'} onValueChange={v => setForm({ ...form, control_plan_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="qcr-new-plan-select"><SelectValue placeholder="Seleziona piano" /></SelectTrigger>
                  <SelectContent>
                    {applicablePlans.length === 0 && (
                      <SelectItem value="none" disabled>
                        {selectedClient
                          ? `Nessun piano per ${selectedClient.name} su questo prodotto`
                          : 'Nessun piano per questo prodotto'}
                      </SelectItem>
                    )}
                    {applicablePlans.map(p => <SelectItem key={p.id} value={p.id}>{p.titolo || `${p.customer_code}/${p.product_code}/${p.revision}`} — rev. {p.revision}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedClient && (
                  <div className="text-[10px] text-slate-500 mt-1">
                    Filtrato per cliente: <span className="font-mono">{selectedClient.code || selectedClient.customer_code || selectedClient.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Numero campioni * (1-100)</Label>
              <Input type="number" min={1} max={100} value={form.sample_count} onChange={e => setForm({ ...form, sample_count: e.target.value })} className="w-32" data-testid="qcr-new-sample-count" />
            </div>

            <div>
              <Label>Situazione di controllo *</Label>
              {!form.control_plan_id && (
                <div className="text-[11px] text-slate-500 mt-1 p-2 bg-slate-50 border border-slate-200 rounded">
                  Seleziona prima un Piano di Controllo per vedere le situazioni disponibili.
                </div>
              )}
              {form.control_plan_id && loadingSituations && (
                <div className="text-[11px] text-slate-500 mt-1 p-2 bg-slate-50 border border-slate-200 rounded">Caricamento…</div>
              )}
              {form.control_plan_id && !loadingSituations && availableSituations.length === 0 && (
                <div className="text-[11px] text-amber-700 mt-1 p-2 bg-amber-50 border border-amber-200 rounded" data-testid="qcr-new-no-situations">
                  Il piano selezionato non ha caratteristiche con applicabilità attiva. Configura le Situazioni nel Piano di Controllo prima di creare un collaudo.
                </div>
              )}
              {form.control_plan_id && !loadingSituations && availableSituations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1" data-testid="qcr-new-situations">
                  {availableSituations.map(s => (
                    <button
                      type="button"
                      key={s.name}
                      onClick={() => setForm({ ...form, control_situation: s.name })}
                      className={`px-3 py-2 rounded border text-sm font-medium transition ${
                        form.control_situation === s.name
                          ? 'bg-indigo-600 text-white border-indigo-700'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
                      }`}
                      data-testid={`qcr-new-situation-${s.code || s.name.replace(/\s+/g, '_')}`}
                    >
                      {s.name}
                      <span className={`ml-1.5 text-[10px] ${form.control_situation === s.name ? 'text-indigo-100' : 'text-slate-500'}`}>
                        ({s.characteristics_count} car.)
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                Le situazioni sono lette dinamicamente dal Piano di Controllo selezionato — solo quelle con almeno una caratteristica attiva.
              </div>
            </div>

            <div>
              <Label>Note controllo</Label>
              <Input value={form.control_notes} onChange={e => setForm({ ...form, control_notes: e.target.value })} placeholder="Note opzionali sul controllo…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenNew(false); setForm(defaultForm()); setProductSearch(''); }}>Annulla</Button>
            <Button onClick={submit} disabled={loading} data-testid="qcr-new-submit">Crea report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
